// Pure vertex-editing helpers for freeform polygon annotations (ENG-2582).
// Kept side-effect free so the move/add/delete math is unit-testable without
// the SVG/pointer layer; PnidRenderer wires these to the handle interactions
// (vertices ride the same resize-handle drag path detected valves use).

export interface Vertex {
  x: number
  y: number
}

export interface Bbox {
  x: number
  y: number
  width: number
  height: number
}

// Axis-aligned bounding box of a vertex ring. The polygon node keeps its bbox
// (x/y/width/height) in sync with the traced outline so selection, zoom-to, and
// the details panel keep working off the same rectangle the rest of the editor
// assumes.
export const bboxOfVertices = (verts: Vertex[]): Bbox => {
  const xs = verts.map((v) => v.x)
  const ys = verts.map((v) => v.y)
  const x = Math.min(...xs)
  const y = Math.min(...ys)
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y }
}

// Translate a single vertex by a delta, leaving the rest untouched.
export const moveVertex = (verts: Vertex[], index: number, dx: number, dy: number): Vertex[] =>
  verts.map((v, i) => (i === index ? { x: v.x + dx, y: v.y + dy } : v))

// Insert a vertex at the midpoint of the edge that starts at `afterIndex`
// (wrapping the last edge back to the first vertex). Lets a user refine a
// boundary by splitting an edge, then dragging the new point.
export const insertVertexAfter = (verts: Vertex[], afterIndex: number): Vertex[] => {
  const a = verts[afterIndex]
  const b = verts[(afterIndex + 1) % verts.length]
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
  return [...verts.slice(0, afterIndex + 1), mid, ...verts.slice(afterIndex + 1)]
}

// Remove a vertex, but never below a triangle — a polygon needs >= 3 points to
// stay a polygon (and to round-trip as a POLYGON ring on save).
export const deleteVertexAt = (verts: Vertex[], index: number): Vertex[] =>
  verts.length <= 3 ? verts : verts.filter((_, i) => i !== index)
